import React, { useRef, useEffect, useCallback } from 'react';
import { Box, Typography, CircularProgress, Paper, Stack, Chip } from '@mui/material';
import type { Message, WidgetState } from '../types/chat.types';
import MessageBubble from './MessageBubble';

const exampleQueries = [
  'How many flights under 10 minutes for drone 102?',
  'What is the total flight time this month?',
  'How do I replace the propellers?',
  'What are the safety protocols for battery maintenance?',
];

interface ChatMessageListProps {
  messages: Message[];
  loading: boolean;
  loadingStatus: string;
  widgetState: WidgetState;
  onExampleClick: (example: string) => void;
}

const ChatMessageList: React.FC<ChatMessageListProps> = ({
  messages,
  loading,
  loadingStatus,
  widgetState,
  onExampleClick,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const userScrolledUpRef = useRef(false);

  // Track whether the user has scrolled away from the bottom
  const handleScroll = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    userScrolledUpRef.current = distanceFromBottom > 100;
  }, []);

  // Auto-scroll only if user hasn't scrolled up (fixes #383)
  useEffect(() => {
    if ((widgetState === 'open' || widgetState === 'fullscreen') && !userScrolledUpRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, widgetState]);

  // Reset scroll-up flag when a new user message is added
  useEffect(() => {
    const lastMsg = messages[messages.length - 1];
    if (lastMsg?.type === 'user') {
      userScrolledUpRef.current = false;
    }
  }, [messages]);

  return (
    <Box
      ref={containerRef}
      onScroll={handleScroll}
      sx={{
        flex: 1,
        overflowY: 'auto',
        p: 2,
        bgcolor: 'grey.50',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      {messages.length === 0 && (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Ask me anything about your fleet data or maintenance procedures.
          </Typography>
          <Typography variant="caption" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
            Try asking:
          </Typography>
          <Stack spacing={1}>
            {exampleQueries.map((example, idx) => (
              <Chip
                key={idx}
                label={example}
                onClick={() => onExampleClick(example)}
                size="small"
                sx={{ cursor: 'pointer', justifyContent: 'flex-start' }}
                variant="outlined"
              />
            ))}
          </Stack>
        </Box>
      )}

      {messages.map((message, idx) => (
        <MessageBubble key={idx} message={message} />
      ))}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
          <Paper sx={{ p: 1.5, bgcolor: 'white', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                {loadingStatus}
              </Typography>
            </Box>
          </Paper>
        </Box>
      )}

      <div ref={messagesEndRef} />
    </Box>
  );
};

export default ChatMessageList;
